import { Controller } from "@hotwired/stimulus"
import { csrfToken } from "../utils/csrf"
import { nativeHaptic } from "../utils/native_bridge"

// Gmail-style swipe-right-to-archive for Messages list rows. Any row inside
// the list carrying data-row-swipe-url (email threads and assistant
// conversations alike) drags right over a green archive layer; past the
// threshold it flies off, the archive endpoint is POSTed and the row
// collapses out of the list (restored if the request fails). Pointer events
// with a horizontal axis lock so vertical scrolling and plain taps are
// untouched. Right-swipe only — deliberately separate from thread-triage,
// which keeps keyboard triage and the in-modal card swipe.
export default class extends Controller {
    CLAIM_PX = 24
    THRESHOLD_RATIO = 0.35

    initialize() {
        this._onPointerDown = this._pointerDown.bind(this)
        this._onPointerMove = this._pointerMove.bind(this)
        this._onPointerUp = this._pointerUp.bind(this)
        this._onPointerCancel = this._pointerCancel.bind(this)
        // Anchors (assistant rows) start a native drag on mouse-swipe.
        this._onDragStart = (e) => {
            if (e.target.closest && e.target.closest("[data-row-swipe-url]")) e.preventDefault()
        }
    }

    connect() {
        this._swipe = null
        this.element.addEventListener("pointerdown", this._onPointerDown)
        this.element.addEventListener("pointermove", this._onPointerMove)
        this.element.addEventListener("pointerup", this._onPointerUp)
        this.element.addEventListener("pointercancel", this._onPointerCancel)
        this.element.addEventListener("dragstart", this._onDragStart)
        this._maybeShowSwipeHint()
    }

    disconnect() {
        this.element.removeEventListener("pointerdown", this._onPointerDown)
        this.element.removeEventListener("pointermove", this._onPointerMove)
        this.element.removeEventListener("pointerup", this._onPointerUp)
        this.element.removeEventListener("pointercancel", this._onPointerCancel)
        this.element.removeEventListener("dragstart", this._onDragStart)
    }

    _pointerDown(e) {
        if (this._swipe || !e.isPrimary || (e.pointerType === "mouse" && e.button !== 0)) return
        let row = e.target.closest("[data-row-swipe-url]")
        if (!row || !this.element.contains(row) || row.dataset.rowSwipeBusy) return
        this._dismissSwipeHint()
        this._swipe = {
            row: row,
            unit: row.closest(".msg-swipe") || row,
            pointerId: e.pointerId,
            startX: e.clientX,
            startY: e.clientY,
            width: row.offsetWidth,
            claimed: false,
            dx: 0
        }
    }

    _pointerMove(e) {
        let s = this._swipe
        if (!s || e.pointerId !== s.pointerId) return
        let dx = e.clientX - s.startX
        let dy = e.clientY - s.startY

        if (!s.claimed) {
            // Axis lock: hand clearly-vertical drags back to the scroller,
            // and ignore leftward drags entirely (right-swipe only).
            if (Math.abs(dy) > 14 && Math.abs(dy) > Math.abs(dx)) {
                this._swipe = null
                return
            }
            if (dx <= -this.CLAIM_PX) {
                this._swipe = null
                return
            }
            if (dx < this.CLAIM_PX || dx < Math.abs(dy) * 1.2) return
            s.claimed = true
            s.unit.classList.add("is-swiping", "is-swipe-right")
            try { s.row.setPointerCapture(e.pointerId) } catch { /* detached row */ }
        }

        e.preventDefault()
        s.dx = Math.max(dx, 0)
        s.row.style.transform = `translateX(${s.dx}px)`
        let past = s.dx >= s.width * this.THRESHOLD_RATIO
        if (past && !s.past) nativeHaptic("light")
        s.past = past
        s.unit.classList.toggle("is-past-threshold", past)
    }

    _pointerUp(e) {
        let s = this._swipe
        if (!s || e.pointerId !== s.pointerId) return
        this._swipe = null
        if (!s.claimed) return

        s.unit.classList.remove("is-swiping", "is-past-threshold")
        this._suppressNextClick()

        if (s.dx >= s.width * this.THRESHOLD_RATIO) {
            this._archive(s)
        } else {
            this._springBack(s)
        }
    }

    _pointerCancel(e) {
        let s = this._swipe
        if (!s || e.pointerId !== s.pointerId) return
        this._swipe = null
        if (s.claimed) {
            s.unit.classList.remove("is-swiping", "is-past-threshold")
            this._springBack(s)
        }
    }

    _springBack(s) {
        s.unit.classList.remove("is-swipe-right")
        s.row.style.transform = ""
    }

    // Optimistic removal: fling + collapse immediately, POST in the
    // background, restore the row only if the request fails.
    _archive(s) {
        nativeHaptic("medium")
        s.row.dataset.rowSwipeBusy = "1"
        s.row.style.transform = `translateX(${s.width + 40}px)`
        s.unit.classList.add("is-leaving")

        fetch(s.row.dataset.rowSwipeUrl, {
            method: "POST",
            headers: { "Accept": "application/json", "X-CSRF-Token": csrfToken() },
            credentials: "same-origin"
        })
            .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
            .then(() => this._collapse(s.unit))
            .catch(() => {
                delete s.row.dataset.rowSwipeBusy
                s.unit.classList.remove("is-leaving", "is-swipe-right")
                s.row.style.transform = ""
                s.row.classList.add("is-action-failed")
                setTimeout(() => s.row.classList.remove("is-action-failed"), 1200)
            })
    }

    _collapse(unit) {
        unit.style.height = `${unit.offsetHeight}px`
        // Force layout so the height transition to 0 actually animates.
        void unit.offsetHeight
        unit.classList.add("is-collapsed")
        unit.style.height = "0px"
        setTimeout(() => unit.remove(), 240)
    }

    // A completed swipe must not double as the row's tap/click (preview
    // modal for emails, navigation for conversation links).
    _suppressNextClick() {
        let swallow = (e) => {
            e.stopPropagation()
            e.preventDefault()
        }
        this.element.addEventListener("click", swallow, { capture: true, once: true })
        setTimeout(() => this.element.removeEventListener("click", swallow, { capture: true }), 400)
    }

    // ----- First-use swipe affordance (touch devices only) -----

    _maybeShowSwipeHint() {
        if (!window.matchMedia("(hover: none) and (pointer: coarse)").matches) return
        if (localStorage.getItem("msgSwipeHintSeen")) return
        let first = this.element.querySelector("[data-row-swipe-url]")
        if (first) (first.closest(".msg-swipe") || first).classList.add("msg-nudge")
    }

    _dismissSwipeHint() {
        localStorage.setItem("msgSwipeHintSeen", "1")
        let nudged = this.element.querySelector(".msg-nudge")
        if (nudged) nudged.classList.remove("msg-nudge")
    }

}
